import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router';
import { Layout } from './components/Layout';
import { Loading } from './components/ui';
import { api, setUnauthorizedHandler } from './lib/api';
import { BrandPage } from './pages/Brand';
import { CandidatesPage } from './pages/Candidates';
import { CatalogPage } from './pages/Catalog';
import { LoginPage } from './pages/Login';
import { NotFound } from './pages/NotFound';
import { OperationsPage } from './pages/Operations';
import { ReviewsPage } from './pages/Reviews';

/** Client-side gating is UX only — every /bff/api call is authorized server-side. */
function Gate() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const loc = useLocation();
  const session = useQuery({ queryKey: ['session'], queryFn: api.session, staleTime: 60_000 });
  useEffect(() => {
    setUnauthorizedHandler(() => {
      qc.setQueryData(['session'], (s: unknown) =>
        s ? { ...(s as object), authenticated: false } : s,
      );
      navigate(`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`, { replace: true });
    });
  }, [qc, navigate, loc.pathname, loc.search]);
  if (session.isPending) return <Loading />;
  if (session.isError || !session.data) return <LoginPage session={null} />;
  if (!session.data.authenticated && loc.pathname !== '/login')
    return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  if (loc.pathname === '/login') return <LoginPage session={session.data} />;
  return (
    <Routes>
      <Route element={<Layout session={session.data} />}>
        <Route index element={<CatalogPage />} />
        <Route path="candidates" element={<CandidatesPage />} />
        <Route path="brands/:ref/*" element={<BrandPage />} />
        <Route path="reviews" element={<ReviewsPage />} />
        <Route path="reviews/:id" element={<ReviewsPage />} />
        <Route path="operations" element={<OperationsPage />} />
        <Route path="operations/runs/:id" element={<OperationsPage />} />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}

export function App() {
  return (
    <BrowserRouter>
      <Gate />
    </BrowserRouter>
  );
}
